import { ApiErrors } from "common/api-errors/errors";
import { FETCHED_ALL_ACTION } from "common/store/property/actions";
import { Investment } from "common/store/investment/types";
import { Payment } from "common/store/payment/types";
import { Property } from "common/store/property/types";
import { User } from "../store/user/types";

import {
  REMOVE_PAYMENT,
  SELECT_DEFAULT_PAYMENT,
  ADD_PAYMENT,
  CONFIRM_INVESTMENT_MUTATION,
  NEW_INVESTMENT_MUTATION,
  ADD_USER_DETAILS,
  ADMIN_LOGIN_MUTATION,
  ADMIN_UPDATE_USER_DETAILS,
  GET_IDNOW_INFO,
  SIGN_UP_MUTATION,
  UPDATE_USER_DETAILS,
  REVOKE_INVESTMENT,
  LOGIN_MUTATION,
  ADD_TO_WHITELIST,
  APPROVE_INVESTMENT,
  ACCEPT_INVESTMENT,
  REJECT_INVESTMENT,
  NEW_TOKEN
} from "./mutations";

import {
  GET_ALL_USERS,
  FIND_INVESTMENTS,
  GET_REVOCABLE,
  GET_CURRENT_USER,
  GET_OFFERINGS,
  CHECK_WHITELIST,
  GET_CURRENT_TRANSACTION,
  GET_IDNOW_INFO_FOR_USER,
} from "./queries";

import {
  WHITELIST_USER_ACTION,
  REGISTER_USER_DETAILS_ACTION,
  LOGIN_ACTION,
  LOGOUT_ACTION,
} from "../store/user/actions";

export enum QueryType {
  Query,
  LazyQuery,
  Mutation
}

export interface QueryDescriptor {
  fetchPolicy?: string;
  input?: (storageData: any) => any;
  onError?: (error: ApiErrors, dispatch: any) => void;
  onSuccess?: (data: any, dispatch: any, storageData: any) => void;
  pollInterval?: number;
  query: any;
  skip?: (storageData: any) => boolean;
  type: QueryType;
}

/* QUERIES */

export const AdminFindInvestments = (status: string) : QueryDescriptor => {
  return {
    type: QueryType.Query,
    query: FIND_INVESTMENTS,
    input: () => {
      return status === "all"
        ? {}
        : { status };
    },
    pollInterval: 25000,
  };
};

export const AdminGetAllUsers: QueryDescriptor = {
  type: QueryType.Query,
  query: GET_ALL_USERS,
  input: () => {
    return {
      firstName: "",
      lastName: "",
      whitelisted: false,
      email: "",
      account: "",
    };
  },
  pollInterval: 25000,
};

export const GetCurrentUser = ({
  onSuccess,
}: any): QueryDescriptor => {
  return {
    type: QueryType.Query,
    query: GET_CURRENT_USER,
    input: (storageUserDetails: any) => {
      return {
        user_id: storageUserDetails.id
      };
    },
    onError: (_: ApiErrors, dispatch: any) => {
      dispatch({ type: LOGOUT_ACTION });
    },
    onSuccess: (data: any, dispatch: any) => {
      let userDetails = new User(data.currentUser);

      dispatch({
        type: REGISTER_USER_DETAILS_ACTION,
        payload: userDetails,
      });

      dispatch({
        type: WHITELIST_USER_ACTION,
        payload: userDetails.whitelisted
      });

      if (onSuccess) {
        onSuccess(data);
      }
    },
    pollInterval: 25000,
  };
}

export const GetOfferings: QueryDescriptor = {
  type: QueryType.Query,
  query: GET_OFFERINGS,
  onSuccess: (data: any, dispatch: any) => {
    dispatch({
      type: FETCHED_ALL_ACTION,
      payload: { properties: data.getCurrentSales.map((property: any) => new Property(property)) },
    });
  },
};

export const GetRevocable: QueryDescriptor = {
  type: QueryType.Query,
  query: GET_REVOCABLE,
  pollInterval: 25000,
};

/* LAZY QUERIES */

export const AdminGetCurrentTransaction: QueryDescriptor = {
  type: QueryType.LazyQuery,
  query: GET_CURRENT_TRANSACTION,
  pollInterval: 6000,
}

export const AdminGetIDNowInfo: QueryDescriptor = {
  type: QueryType.LazyQuery,
  query: GET_IDNOW_INFO_FOR_USER,
}

export const CheckWhitelist = ({
  onSuccess,
}: any): QueryDescriptor => {
  return {
    fetchPolicy: 'network-only',
    type: QueryType.LazyQuery,
    query: CHECK_WHITELIST,
    onSuccess: (data: any) => {
      onSuccess(data);
    },
  };
}

export const GetCurrentUserLazy = ({
  onSuccess,
}: any): QueryDescriptor => {
  return {
    fetchPolicy: 'network-only',
    type: QueryType.LazyQuery,
    query: GET_CURRENT_USER,
    input: (storageUserDetails: any) => {
      return {
        user_id: storageUserDetails.id
      };
    },
    onSuccess: (data: any, dispatch: any) => {
      let userDetails = new User(data.currentUser);

      dispatch({
        type: REGISTER_USER_DETAILS_ACTION,
        payload: userDetails,
      });

      dispatch({
        type: WHITELIST_USER_ACTION,
        payload: userDetails.whitelisted
      });

      onSuccess(data);
    },
  };
}

/* MUTATIONS */

export const AcceptInvestment = ({
  onError,
}: any): QueryDescriptor => {
  return {
    type: QueryType.Mutation,
    query: ACCEPT_INVESTMENT,
    onError: (error: ApiErrors) => {
      onError(error);
    },
  };
}

export const AddPayment = ({
  onSuccess,
}: any): QueryDescriptor => {
  return {
    type: QueryType.Mutation,
    query: ADD_PAYMENT,
    onSuccess: (data: any, dispatch: any, storageData: any) => {
      let userDetails = new User(storageData);
      if (!userDetails.payment_accounts) {
        userDetails.payment_accounts = [];
      }
      userDetails.payment_accounts.push(new Payment(data.addPayment));

      if (userDetails.payment_accounts.length === 1) {
        userDetails.default_payment = data.addPayment.id;
      }

      dispatch({
        type: REGISTER_USER_DETAILS_ACTION,
        payload: userDetails,
      });

      onSuccess(data);
    },
  };
}

export const AddUserDetails= ({
  onError,
  onSuccess,
}: any): QueryDescriptor => {
  return {
    type: QueryType.Mutation,
    query: ADD_USER_DETAILS,
    onError: (error: ApiErrors) => {
      onError(error);
    },
    onSuccess: (data: any, dispatch: any) => {
      dispatch({
        type: REGISTER_USER_DETAILS_ACTION,
        payload: new User(data.addUserDetails),
      });

      onSuccess(data);
    },
  };
}

export const AddToWhitelist = ({
  onSuccess,
}: any): QueryDescriptor => {
  return {
    type: QueryType.Mutation,
    query: ADD_TO_WHITELIST,
    onSuccess: (data: any) => {
      onSuccess(data);
    },
  };
}

export const AdminLogin = ({
  onError,
  onSuccess,
}: any): QueryDescriptor => {
  return {
    type: QueryType.Mutation,
    query: ADMIN_LOGIN_MUTATION,
    onError: (error: ApiErrors) => {
      onError(error);
    },
    onSuccess: (data: any, dispatch: any) => {
      dispatch({
        type: LOGIN_ACTION,
        payload: { 
          token: data.adminLogin.token
        }
      });

      onSuccess(data);
    },
  };
}

export const AdminRevokeInvestment = ({
  onError,
}: any): QueryDescriptor => {
  return {
    type: QueryType.Mutation,
    query: REVOKE_INVESTMENT,
    onError: (error: ApiErrors) => {
      onError(error);
    },
  };
}

export const AdminUpdateUser = ({
  onError,
  onSuccess,
}: any): QueryDescriptor => {
  return {
    type: QueryType.Mutation,
    query: ADMIN_UPDATE_USER_DETAILS,
    onError: (error: ApiErrors) => {
      onError(error);
    },
    onSuccess: (data: any) => {
      onSuccess(data);
    },
  };
}

export const ApproveInvestment = ({
  onError,
}: any): QueryDescriptor => {
  return {
    type: QueryType.Mutation,
    query: APPROVE_INVESTMENT,
    onError: (error: ApiErrors) => {
      onError(error);
    },
  };
}

export const ConfirmInvestment = ({
  onError,
}: any): QueryDescriptor => {
  return {
    type: QueryType.Mutation,
    query: CONFIRM_INVESTMENT_MUTATION,
    onError: (error: ApiErrors) => {
      onError(error);
    },
  };
}

export const GetIDNowInfo: QueryDescriptor = {
  type: QueryType.Mutation,
  query: GET_IDNOW_INFO,
  onSuccess: (data: any, dispatch: any, storageData: any) => {
    const userDetails = new User({
      ...storageData,
      ...data.getIdNowIdent
    });

    dispatch({
      type: REGISTER_USER_DETAILS_ACTION,
      payload: userDetails,
    });
  },
}

export const LogIn = ({
  onError,
}: any): QueryDescriptor => {
  return {
    type: QueryType.Mutation,
    query: LOGIN_MUTATION,
    onError: (error: ApiErrors) => {
      onError(error);
    },
    onSuccess: (data: any, dispatch: any) => {
      dispatch({
        type: LOGIN_ACTION,
        payload: {
          token: data.logIn.token
        },
      });
    },
  };
}

export const NewInvestment= ({
  onError,
  onSuccess,
}: any): QueryDescriptor => {
  return {
    type: QueryType.Mutation,
    query: NEW_INVESTMENT_MUTATION,
    onError: (error: ApiErrors) => {
      onError(error);
    },
    onSuccess: (data: any, dispatch: any, storageData: any) => {
      const userDetails = new User({ ...storageData });
      userDetails.investments.push(new Investment(data.newInvestment));
  
      dispatch({
        type: REGISTER_USER_DETAILS_ACTION,
        payload: userDetails,
      });

      onSuccess(data);
    },
  };
}

export const NewToken = ({
  onError,
  onSuccess,
}: any): QueryDescriptor => {
  return {
    type: QueryType.Mutation,
    query: NEW_TOKEN,
    onError: (error: ApiErrors) => {
      onError(error);
    },
    onSuccess: (data: any) => {
      onSuccess(data);
    },
  };
}

export const RejectInvestment = ({
  onError,
}: any): QueryDescriptor => {
  return {
    type: QueryType.Mutation,
    query: REJECT_INVESTMENT,
    onError: (error: ApiErrors) => {
      onError(error);
    },
  };
}

export const RemovePayment = ({
  onError,
  onSuccess,
}: any): QueryDescriptor => {
  return {
    type: QueryType.Mutation,
    query: REMOVE_PAYMENT,
    onError: (error: ApiErrors) => {
      onError(error);
    },
    onSuccess: (data: any) => {
      onSuccess(data);
    },
  };
}

export const RevokeInvestment = ({
  onError,
  onSuccess,
}: any): QueryDescriptor => {
  return {
    type: QueryType.Mutation,
    query: REVOKE_INVESTMENT,
    onError: (error: ApiErrors) => {
      onError(error);
    },
    onSuccess: (data: any, dispatch: any, storageData: any) => {
      let userDetails = new User(JSON.parse(JSON.stringify(storageData)));
      userDetails.investments.forEach(investment => {
        if (investment.id === data.revokeInvestment.id) {
          investment.status = data.revokeInvestment.status;
        }
      });

      dispatch({
        type: REGISTER_USER_DETAILS_ACTION,
        payload: userDetails,
      });

      onSuccess(data);
    },
  };
}

export const SelectDefaultPayment= ({
  onError,
  onSuccess,
}: any): QueryDescriptor => {
  return {
    type: QueryType.Mutation,
    query: SELECT_DEFAULT_PAYMENT,
    onError: (error: ApiErrors) => {
      onError(error);
    },
    onSuccess: (data: any) => {
      onSuccess(data);
    },
  };
}

export const SignUp = ({
  onError,
}: any): QueryDescriptor => {
  return {
    type: QueryType.Mutation,
    query: SIGN_UP_MUTATION,
    onError: (error: ApiErrors) => {
      onError(error);
    },
    onSuccess: (data: any, dispatch: any) => {
      dispatch({
        type: LOGIN_ACTION,
        payload: {
          token: data.signUp.token
        },
      });
    },
  };
}

export const UpdateUser = ({
  onSuccess,
}: any): QueryDescriptor => {
  return {
    type: QueryType.Mutation,
    query: UPDATE_USER_DETAILS,
    onSuccess: (data: any, dispatch: any) => {
      dispatch({
        type: REGISTER_USER_DETAILS_ACTION,
        payload: new User(data.updateUserDetails),
      });

      onSuccess(data);
    },
  };
}
